import type { TraceEdge, TraceGraph, TraceNode } from '@gsls/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { formatDateTime, formatQuantity, humanise } from '../lib/format';
import { DataTable, type DataColumn } from './DataTable';
import { Badge, Button, Card, CardBody, ErrorNote, Spinner, Tooltip } from './ui';

const KIND_TONES: Record<TraceNode['kind'], 'green' | 'accent' | 'hazard' | 'muted'> = {
  lot: 'green',
  batch: 'accent',
  recovery: 'hazard',
  supplier: 'muted',
};

const RELATION_LABELS: Record<string, string> = {
  produced: 'produced by',
  consumed: 'consumed',
  used_in: 'used in',
  recovered_from: 'recovered from',
  sourced_from: 'sourced from',
};

/** One related node, with the edge that connects it to the lot being traced. */
interface TraceEntry {
  node: TraceNode;
  edge: TraceEdge | undefined;
}

const TRACE_COLUMNS: DataColumn<TraceEntry>[] = [
  {
    key: 'relationship',
    header: 'Relationship',
    cell: (entry) => (
      <span className="text-xs">
        {entry.edge ? (RELATION_LABELS[entry.edge.relation] ?? entry.edge.relation) : '—'}
      </span>
    ),
  },
  {
    key: 'what',
    header: 'What',
    primary: true,
    cell: (entry) => (
      <>
        <Badge tone={KIND_TONES[entry.node.kind]}>{entry.node.kind}</Badge>{' '}
        <span className="font-mono text-xs">{entry.node.label}</span>
        <br />
        <span className="text-ink-body">{entry.node.title}</span>
      </>
    ),
  },
  {
    key: 'detail',
    header: 'Detail',
    cell: (entry) => (
      <span className="text-xs">
        {entry.node.detail ?? '—'}
        {entry.node.status ? (
          <>
            <br />
            <span className="text-ink-muted">{humanise(entry.node.status)}</span>
          </>
        ) : null}
      </span>
    ),
  },
  {
    key: 'quantity',
    header: 'Quantity',
    align: 'right',
    cell: (entry) => (
      <span className="whitespace-nowrap text-xs">
        {entry.edge?.quantity ? formatQuantity(entry.edge.quantity, entry.edge.uom) : '—'}
      </span>
    ),
  },
  {
    key: 'when',
    header: 'When',
    cell: (entry) => (
      <span className="whitespace-nowrap text-xs">{formatDateTime(entry.edge?.occurredAt)}</span>
    ),
  },
];

/**
 * The genealogy of a lot.
 *
 * Rendered as grouped lists rather than a drawn graph: the question being asked
 * is always "where did this come from?" or "where did it go?", and a list
 * answers it without needing a layout engine.
 */
export function TracePanel({
  start,
  onClose,
}: {
  start: { kind: 'lot'; id: string } | { kind: 'batch'; id: string };
  onClose: () => void;
}) {
  const [depth, setDepth] = useState(6);

  const trace = useQuery({
    queryKey: ['trace', start.kind, start.id, depth],
    queryFn: () =>
      start.kind === 'lot'
        ? api.trace.lot(start.id, { depth })
        : api.trace.batch(start.id, { depth }),
  });

  if (trace.isPending) {
    return (
      <Card className="mb-6">
        <Spinner label="Walking the genealogy…" />
      </Card>
    );
  }

  if (trace.isError || !trace.data) {
    return (
      <Card className="mb-6">
        <CardBody>
          <ErrorNote>
            Could not trace that {start.kind}. A batch with no output yet has nothing to trace.
          </ErrorNote>
          <div className="mt-3">
            <Button tone="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  const graph: TraceGraph = trace.data;
  const byKey = new Map(graph.nodes.map((node) => [node.key, node]));

  /**
   * Edges are emitted along the direction material flows, so ancestry is simply
   * "follow the arrows backwards from the root" and descent is "follow them
   * forwards". Classifying by the relation name alone would not work: `produced`
   * is upstream from an output lot and downstream from a consumed one.
   */
  const forward = new Set<string>([graph.root.key]);
  const backward = new Set<string>([graph.root.key]);

  for (let pass = 0; pass < graph.nodes.length; pass += 1) {
    for (const edge of graph.edges) {
      if (forward.has(edge.from)) forward.add(edge.to);
      if (backward.has(edge.to)) backward.add(edge.from);
    }
  }

  /**
   * Listed per node, not per edge: a lot that went into two batches, or a batch
   * that consumed six lots, would otherwise repeat itself and read as though the
   * material had moved more than it did.
   */
  const relatedNodes = (keys: Set<string>, side: 'up' | 'down') =>
    [...keys]
      .filter((key) => key !== graph.root.key)
      .map((key) => byKey.get(key))
      .filter((node): node is TraceNode => Boolean(node))
      .map((node) => {
        const connecting = graph.edges.find((edge) =>
          side === 'up' ? edge.from === node.key && backward.has(edge.to) : edge.to === node.key && forward.has(edge.from),
        );
        return { node, edge: connecting };
      })
      .sort((left, right) => left.node.kind.localeCompare(right.node.kind) || left.node.label.localeCompare(right.node.label));

  const upstream = relatedNodes(backward, 'up');
  const downstream = relatedNodes(forward, 'down');

  return (
    <Card className="mb-6 border-l-4 border-l-brand">
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand-deep">
              Traceability
              <Tooltip
                subject="Traceability"
                text="The recorded chain of material behind and after this lot: which batches consumed it, which batches and recoveries produced what it is made from, and which supplier it came from. Nothing is entered for this — it is read out of the stock history."
              />
            </p>
            <h2 className="mt-1 text-lg font-bold text-ink">
              {graph.root.label} — {graph.root.title}
            </h2>
            <p className="mt-1 text-sm text-ink-body">
              {graph.nodes.length} node{graph.nodes.length === 1 ? '' : 's'} ·{' '}
              {graph.edges.length} relationship{graph.edges.length === 1 ? '' : 's'}
              {graph.truncated
                ? ' · the walk stopped at the requested depth, so there is more beyond this'
                : ' · the whole chain was walked'}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-ink-muted">
                Depth
              </span>
              <select
                name="traceDepth"
                className="rounded-sm border border-surface-border px-2 py-1 text-sm"
                value={depth}
                onChange={(event) => setDepth(Number(event.target.value))}
              >
                {[1, 2, 3, 4, 6, 9, 12].map((option) => (
                  <option key={option} value={option}>
                    {option} hop{option === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </label>
            <span className="pb-1.5">
              <Tooltip
                subject="Depth"
                text="How many steps of the chain to follow. One hop reaches what made this lot directly; more hops follow the material further back, through the batches and recoveries behind it. A long chain is cut off at the limit and says so."
              />
            </span>
            <Button tone="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-1.5">
          <h3 className="text-sm font-bold uppercase tracking-wide text-ink-muted">
            Where it came from
          </h3>
          <Tooltip
            subject="Where it came from"
            text="Everything this lot is downstream of: the batch that made it, the material that went into that batch, any recovered material that came off it, and where the raw material was bought. This is the direction a recall runs in."
          />
        </div>
        <div className="mt-2">
          <DataTable
            rows={upstream}
            columns={TRACE_COLUMNS}
            rowKey={(entry) => entry.node.key}
            empty="Nothing upstream — this is where the chain starts."
          />
        </div>

        <div className="mt-5 flex items-center gap-1.5">
          <h3 className="text-sm font-bold uppercase tracking-wide text-ink-muted">
            Where it went
          </h3>
          <Tooltip
            subject="Where it went"
            text="Everything this lot is upstream of: batches that drew on it, and recovered material it became part of. Empty is normal and good news for a finished product — nothing was made from it."
          />
        </div>
        <div className="mt-2">
          <DataTable
            rows={downstream}
            columns={TRACE_COLUMNS}
            rowKey={(entry) => entry.node.key}
            empty="Nothing downstream — this is where the chain ends."
          />
        </div>
      </CardBody>
    </Card>
  );
}
